<template>
    <model-form
        :title="taskGroup && taskGroup.id ? `Edit ${taskGroup.displayName}` : 'Create Task Group'"
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    >
        <template v-slot:dayFieldDescription>
            <v-col v-if="specifyRelativeToDay0Field">
                <span v-if="specifyRelativeToDay0Field.value">
                    <span v-if="experiment">
                        Task dates will be relative to the experiment day 0 of
                        {{ experiment.day0.toISODate() }}
                    </span>
                    <span v-else> Task day will be based on experiment day 0. </span>
                </span>
                <span v-else>
                    Task dates will default to {{ dateField.value }} regardless of experiment day 0.
                </span>
            </v-col>
        </template>
    </model-form>
</template>
<script>
import { defineComponent } from "vue";

import ModelForm from "../common/models/ModelForm.vue";
import {
    buildMultiSelectField,
    buildNumberField,
    buildSelectField,
    buildTextField,
} from "@/lib/vueModelUtils";
import {
    buildDayByDateForExperimentStartDateField,
    getDateTimeForISODateOnlyString,
} from "@/lib/dateUtils";
import {
    buildReadoutGroupOptions,
    buildTaskThingDateEstimateOptions,
    validateRelativeToTaskEstimateTaskGroup,
} from "@/lib/taskUtils";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";
import { useToast } from "vue-toastification";

export default defineComponent({
    name: "task-group-form",

    props: {
        taskGroup: {
            type: Object,
            required: false,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskThingDateEstimates: {
            type: Array,
            required: false /* for protocol task groups */,
        },
        experiment: {
            type: Object,
            required: false,
        },
    },

    components: {
        ModelForm,
    },

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            taskGroupTypeField: null,
            dayField: null,
            relativeDateField: null,
            dateField: null,
            nameField: null,
            readoutGroupIdsField: null,
            taskThingEstimateDateIdField: null,
            dayAfterReferenceField: null,
            formFields: [],
        };
    },

    mounted() {
        this.buildFormFields();
    },

    methods: {
        buildFormFields() {
            const isDateSpecified = this.taskGroup && !_.isNil(this.taskGroup.date);
            const isDaySpecified = this.taskGroup && !_.isNil(this.taskGroup.day);

            const taskGroupTypeOptions = [
                {
                    title: "Specify day relative to day 0",
                    value: "relativeToDay0",
                },
                {
                    title: "Specify a specific date",
                    value: "specificDate",
                },
            ];

            if (this.taskThingDateEstimates) {
                taskGroupTypeOptions.push({
                    title: "Specify a date relative to an estimate",
                    value: "relativeToTaskEstimate",
                });
            }

            this.taskGroupTypeField = {
                fieldType: "radioButtons",
                fieldDisplayName: "Task Group Type",
                options: taskGroupTypeOptions,
                changed: () => {
                    this.updateFormFields();
                },
                value: isDateSpecified
                    ? "specificDate"
                    : isDaySpecified
                      ? "relativeToDay0"
                      : "relativeToTaskEstimate",
                cols: 12,
            };

            this.dayField = buildTextField({
                label: "Day",
                value: this.taskGroup.day,
            });

            if (this.experiment) {
                this.dayField.changed = (value) => {
                    const date =
                        value === ""
                            ? this.experiment.day0
                            : this.experiment.day0.plus({ days: value });
                    this.relativeDateField.value = date.toISODate();
                };

                this.relativeDateField = buildDayByDateForExperimentStartDateField(
                    this.taskGroup.day,
                    this.experiment.day0,
                    (value) => {
                        this.dayField.value = value;
                    }
                );
            }

            this.dateField = {
                fieldType: "date",
                fieldDisplayName: "Date",
                cols: 12,
                value: this.getDefaultAnchorDateValue(),
            };

            this.nameField = buildTextField({
                label: "Name",
                value: this.taskGroup.name,
                required: true,
            });

            const readoutGroupOptions = buildReadoutGroupOptions(this.readoutGroups);
            this.readoutGroupIdsField = buildMultiSelectField({
                name: "Readout Groups",
                options: readoutGroupOptions,
                values: this.taskGroup.readoutGroupIds || [],
            });

            if (this.taskThingDateEstimates) {
                const taskThingDateEstimateOptions = buildTaskThingDateEstimateOptions(
                    this.taskThingDateEstimates
                );
                this.taskThingEstimateDateIdField = buildSelectField({
                    name: "Thing Estimated Date",
                    options: taskThingDateEstimateOptions,
                    value: this.taskGroup.taskThingDateEstimateId,
                });

                this.dayAfterReferenceField = buildNumberField({
                    label: "Day after reference",
                    value: this.taskGroup.dayAfterReference,
                });
            }

            this.updateFormFields();
        },
        updateFormFields() {
            const formFields = [this.taskGroupTypeField];

            switch (this.taskGroupTypeField.value) {
                case "relativeToDay0":
                    formFields.push(this.dayField);
                    if (this.experiment) {
                        formFields.push(this.relativeDateField);
                    }
                    break;
                case "specificDate":
                    formFields.push(this.dateField);
                    break;
                case "relativeToTaskEstimate":
                    if (this.taskThingDateEstimates) {
                        formFields.push(this.taskThingEstimateDateIdField);
                        formFields.push(this.dayAfterReferenceField);
                    }
                    break;
            }

            formFields.push(this.nameField, this.readoutGroupIdsField);

            this.formFields = formFields;
        },
        getDefaultAnchorDateValue() {
            const date = this.taskGroup ? this.taskGroup.date : null;
            if (date) {
                return getDateTimeForISODateOnlyString(date).toISODate();
            } else {
                return null;
            }
        },
        async save() {
            await withLoadingScreen(async () => {
                const taskGroup = {
                    ...this.taskGroup,
                    name: this.nameField.value,
                    readoutGroupIds: this.readoutGroupIdsField.values,
                    // clear out the existing values and set them based on the chosen type
                    day: null,
                    date: null,
                    taskThingDateEstimateId: null,
                    dayAfterReference: null,
                };

                switch (this.taskGroupTypeField.value) {
                    case "relativeToDay0":
                        taskGroup.day = this.dayField.value;
                        break;
                    case "specificDate":
                        // Note: saves the date as "YYYY-MM-dd", stored in the DB as UTC
                        taskGroup.date = this.dateField.value;
                        break;
                    case "relativeToTaskEstimate":
                        taskGroup.taskThingDateEstimateId = this.taskThingEstimateDateIdField.value;
                        taskGroup.dayAfterReference = this.dayAfterReferenceField.value;
                        const error = validateRelativeToTaskEstimateTaskGroup(taskGroup);
                        if (error) {
                            this.toast.error(error);
                            return;
                        }
                        break;
                    default:
                        // shouldn't get here but just in case, don't save a bad TaskGroup.
                        this.toast.error("Please choose a task group type");
                        return;
                }

                if (this.taskGroup.id) {
                    this.$emit("update", taskGroup);
                } else {
                    this.$emit("create", taskGroup);
                }
            });
        },
        cancel() {
            this.$emit("closed");
        },
        close() {
            this.$emit("closed");
        },
    },
});
</script>
